import { isValidElement } from 'react';
import { DataListItem } from '@brighte/form-components';
import {
  ApplicationInputType,
  BusinessDetailsType,
  EnterpriseStructure,
  EntityStructureOption,
  FINANCIAL_PRODUCT,
  isExtendedStructure,
} from '@brighte/validation';
import { useGetSteps } from '@pages/ApplicationForm/hooks';
import { useApplicationData, useStepperActions } from '@provider';
import { APPLICATION_STEPS, BUSINESS_DETAILS, LABELS } from '@utils';

function getContactSection(
  contact: Record<string, unknown>,
  title: string,
  addressLabel: string,
): { title: string; items: DataListItem[] } {
  const address = contact.residentialAddress as Record<string, unknown> | undefined;
  const items: DataListItem[] = [
    {
      label: 'Name',
      value: [contact.firstName, contact.middleName, contact.lastName].filter(Boolean).join(' '),
    },
  ];
  if (address?.fullAddress) {
    items.push({ label: addressLabel, value: address.fullAddress as string });
  }
  return { title, items };
}

function getTrustDeedSection(enterprise: Record<string, unknown>): { title: string; items: DataListItem[] } | null {
  const trustDeedFiles = enterprise.trustDeedFiles as File[] | undefined;
  const trustDeedDocumentIds = enterprise.trustDeedDocumentIds as string[] | undefined;
  const trustDeedDocumentNames = enterprise.trustDeedDocumentNames as string[] | undefined;
  const fileCount = trustDeedFiles?.length ?? 0;
  const docCount = trustDeedDocumentIds?.length ?? 0;
  const totalCount = fileCount > 0 ? fileCount : docCount;

  if (totalCount === 0) return null;

  const fileNames = fileCount > 0 ? trustDeedFiles!.map(file => file.name) : (trustDeedDocumentNames ?? []);
  const value = fileNames.length > 0 ? fileNames.join(', ') : `${totalCount} document(s) uploaded`;
  return { title: 'Trust Deed', items: [{ label: 'Documents', value }] };
}

function getExtendedBusinessSections(
  enterprise: Record<string, unknown>,
): Array<{ title: string; items: DataListItem[] }> {
  const {
    ENTITY_STRUCTURE,
    LEGAL_BUSINESS_NAME,
    TRADING_NAME,
    ABN,
    BUSINESS_PHONE,
    BUSINESS_EMAIL,
    BUSINESS_WEBSITE,
    YEARS_IN_TRADING,
    NUMBER_OF_EMPLOYEES,
    INDUSTRY_CATEGORY,
    PROPERTY_STATUS,
  } = LABELS;

  const sections: Array<{ title: string; items: DataListItem[] }> = [];

  // Business details section
  sections.push({
    title: 'Business Details',
    items: [
      { label: ENTITY_STRUCTURE, value: enterprise.structure as string },
      { label: LEGAL_BUSINESS_NAME, value: enterprise.legalEntityName as string },
      { label: TRADING_NAME, value: enterprise.tradingName as string },
      { label: ABN, value: enterprise.abn as string },
      { label: BUSINESS_PHONE, value: enterprise.businessPhone as string },
      { label: BUSINESS_EMAIL, value: enterprise.businessEmail as string },
      ...(enterprise.businessWebsite ? [{ label: BUSINESS_WEBSITE, value: enterprise.businessWebsite as string }] : []),
      { label: YEARS_IN_TRADING, value: enterprise.yearsInTrading as string },
      { label: NUMBER_OF_EMPLOYEES, value: enterprise.numberOfEmployees as string },
      { label: INDUSTRY_CATEGORY, value: enterprise.industryCategory as string },
      { label: PROPERTY_STATUS, value: enterprise.propertyStatus as string },
    ],
  });

  // Primary contact section
  const primaryContact = enterprise.primaryContact as Record<string, unknown> | undefined;
  if (primaryContact) {
    sections.push(getContactSection(primaryContact, 'Primary Contact', 'Residential address'));
  }

  // Billing contact section
  if (!enterprise.billingContactSameAsPrimary && enterprise.billingContact) {
    sections.push(
      getContactSection(enterprise.billingContact as Record<string, unknown>, 'Billing Contact', 'Billing address'),
    );
  } else {
    sections.push({
      title: 'Billing Contact',
      items: [{ label: 'Same as primary contact', value: 'Yes' }],
    });
  }

  // Trust deed section (Trust structure only)
  if (enterprise.structure === EnterpriseStructure.Trust) {
    const trustDeedSection = getTrustDeedSection(enterprise);
    if (trustDeedSection) {
      sections.push(trustDeedSection);
    }
  }

  return sections;
}

function useBusinessDetailsSummary(): Array<{ title: string; items: DataListItem[] | [] }> | [] {
  const businessDetails = useApplicationData('BusinessDetails');

  const { ENTITY_STRUCTURE, BUSINESS_NAME, OFFICE_ADDRESS, ASSOCIATION_NUMBER } = LABELS;

  if (!businessDetails || !businessDetails?.enterprise || !businessDetails?.applicant) return [];

  const { enterprise, applicant } = businessDetails;

  const hasExtendedFields = isExtendedStructure(enterprise.structure);

  if (hasExtendedFields) {
    return [
      {
        title: 'Applicant',
        items: [
          {
            label: 'Name',
            value: [applicant.firstName, applicant.middleName, applicant.lastName].filter(Boolean).join(' '),
          },
        ],
      },
      ...getExtendedBusinessSections(enterprise),
    ];
  }

  const enterpriseItems: DataListItem[] = [];

  if (
    enterprise.structure === EnterpriseStructure.IncorporatedAssociation ||
    enterprise.structure === EnterpriseStructure.UnincorporatedAssociation
  ) {
    enterpriseItems.push({
      label: ASSOCIATION_NUMBER,
      value: enterprise.associationNumber,
    });
  }

  return [
    {
      title: 'Applicant',
      items: [
        {
          label: 'Name',
          value: [applicant.firstName, applicant.middleName, applicant.lastName].filter(Boolean).join(' '),
        },
      ],
    },
    {
      title: 'Enterprise',
      items: [
        {
          label: ENTITY_STRUCTURE,
          value: enterprise.structure,
        },
        {
          label: BUSINESS_NAME,
          value: enterprise.legalEntityName,
        },
        ...enterpriseItems,
        {
          label: OFFICE_ADDRESS,
          value: enterprise.registeredAddress?.fullAddress,
        },
      ],
    },
  ];
}
const generateStructureOptions = (financeProductId?: string): EntityStructureOption[] => {
  const { ACT_BUSINESS_STRUCTURE_OPTIONS, SME_STRUCTURE_OPTIONS, ACT_COMMUNITY_STRUCTURE_OPTIONS } = BUSINESS_DETAILS;

  const options: Record<string, EntityStructureOption[]> = {
    [FINANCIAL_PRODUCT.ACT_COMMUNITY]: ACT_COMMUNITY_STRUCTURE_OPTIONS,
    [FINANCIAL_PRODUCT.SME]: SME_STRUCTURE_OPTIONS,
  };

  return (financeProductId && options[financeProductId]) || ACT_BUSINESS_STRUCTURE_OPTIONS;
};
function useBeforeNextStep() {
  const steps = useGetSteps();
  const { onNext } = useStepperActions();
  const { BusinessDetails: businessDetails } = useApplicationData(['BusinessDetails']);

  const ownershipDetailsIndex = steps.findIndex(
    step => isValidElement(step) && step.key === APPLICATION_STEPS.OWNERSHIP_DETAILS,
  );

  const handleBeforeNextStep = (formValues: ApplicationInputType) => {
    // If structure has changed, navigate to OwnershipDetails
    const latestStructure = (formValues as BusinessDetailsType)?.enterprise?.structure;
    const existingStructure = businessDetails?.enterprise?.structure;

    // Only redirect if structure has actually changed (not on first visit when existingStructure is undefined)
    if (ownershipDetailsIndex !== -1 && existingStructure !== undefined && latestStructure !== existingStructure) {
      onNext(ownershipDetailsIndex);
      return true;
    }

    return false;
  };

  return { handleBeforeNextStep };
}

export { useBeforeNextStep, useBusinessDetailsSummary, generateStructureOptions };
